import type React from 'react';
import { Navigate, useLocation } from 'react-router';

import { getRole } from 'api/tokenStorage';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { ErrorPage } from 'modules/E404/Error';
import { resolveSessionUnavailableCode } from 'modules/E404/errorCatalog';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

type RoleGuardProps = {
  requiredRole: number;
  children: React.ReactElement;
};

const getHomeByActiveRole = (
  roleString: string | undefined,
  sessionRoles: number[] = [],
) => {
  if (roleString === 'tutor' && sessionRoles.includes(1)) return '/teacher';
  if (roleString === 'student' && sessionRoles.includes(2)) return '/profile/tasks';
  if (sessionRoles.includes(1)) return '/teacher';
  if (sessionRoles.includes(2)) return '/profile/tasks';
  return '/login';
};

const resolveCurrentRoleId = (
  roleString: string | undefined,
  sessionRoles: number[] = [],
): number | null => {
  const cookieRoleId =
    roleString === 'tutor' ? 1 : roleString === 'student' ? 2 : null;

  if (cookieRoleId != null && sessionRoles.includes(cookieRoleId)) {
    return cookieRoleId;
  }
  if (sessionRoles.includes(1)) return 1;
  if (sessionRoles.includes(2)) return 2;
  return cookieRoleId;
};

const SessionStatus: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: 24, textAlign: 'center' }} role='status' aria-live='polite'>
    {children}
  </div>
);

export const RoleGuard: React.FC<RoleGuardProps> = ({ requiredRole, children }) => {
  const location = useLocation();
  const { data: session, isLoading, isFetching, isError, error, refetch } = useAuthSession();
  const isAuthenticated = isAuthSessionActive(session);
  const sessionRoles = session?.roles ?? [];

  if (isLoading || (isFetching && !session)) {
    return (
      <SessionStatus>
        <BrandLoader label='Загрузка...' />
      </SessionStatus>
    );
  }

  if (isError && !session) {
    return (
      <ErrorPage
        code={resolveSessionUnavailableCode(error)}
        embedded
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  if (!isAuthenticated) {
    const from = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(from)}`}
        replace
        state={{ from }}
      />
    );
  }

  if (!sessionRoles.includes(requiredRole)) {
    return (
      <Navigate to={getHomeByActiveRole(getRole(), sessionRoles)} replace />
    );
  }

  const roleString = getRole();
  const currentRoleId = resolveCurrentRoleId(roleString, sessionRoles);

  // Mismatch: send user to the cabinet that matches their active role cookie
  // (not always /teacher). Avoids Navigate loops when cookie=student but path=/teacher.
  if (currentRoleId !== requiredRole) {
    return (
      <Navigate to={getHomeByActiveRole(roleString, sessionRoles)} replace />
    );
  }

  return children;
};
